import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { getApiBaseUrl } from '@/lib/api';

type VerifyState = 'loading' | 'success' | 'error';

export default function AuthVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>('loading');
  const [error, setError] = useState<string | null>(null);
  // Ref to prevent double verification (React StrictMode or re-renders)
  const verificationAttempted = useRef(false);

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent duplicate verification attempts
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      const token = searchParams.get('token');

      if (!token) {
        setState('error');
        setError('No verification token provided');
        return;
      }

      try {
        // Auto-detect browser timezone for new user registration
        const browserTimezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Call the verify endpoint with browser timezone
        // Build query string manually since getApiBaseUrl() may return a relative URL
        const baseUrl = getApiBaseUrl();
        const params = new URLSearchParams();
        params.set('token', token);
        if (browserTimezone) {
          params.set('timezone', browserTimezone);
        }
        const verifyUrl = `${baseUrl}/auth/verify?${params.toString()}`;

        const response = await fetch(verifyUrl);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Verification failed');
        }

        // Sign in with the custom token
        await signInWithCustomToken(auth, data.customToken);

        // Clear the stored email
        localStorage.removeItem('emailForSignIn');

        setState('success');

        // Redirect after a short delay
        setTimeout(() => {
          navigate(data.redirectTo || '/dashboard');
        }, 2000);
      } catch (err) {
        setState('error');
        setError(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="animate-spin h-8 w-8 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-display font-bold text-dark mb-4">
              Verifying your link...
            </h1>
            <p className="text-dark-500 font-body">
              Please wait while we sign you in.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-2xl font-display font-bold text-dark mb-4">
              Verification failed
            </h1>
            <p className="text-dark-500 font-body mb-6">{error}</p>
            <Link
              to="/login"
              className="inline-block bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Try again
            </Link>
          </div>
          <Link
            to="/"
            className="block text-center text-dark-500 mt-6 hover:text-primary transition-colors font-body"
          >
            &larr; Back to home
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-dark mb-4">
            You&apos;re signed in!
          </h1>
          <p className="text-dark-500 font-body">
            Redirecting you to your dashboard...
          </p>
        </div>
      </div>
    </div>
  );
}
